import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SYNC_STAGES, type SyncProgress } from '@fieldops/shared'
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  CommandBar,
  CommandMeta,
  CommandTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Page,
  Select,
  SkeletonRows,
  Textarea,
  Toggle,
  ToggleRow,
} from '../components/ui.js'
import { Choice } from '../components/repeat/RuleFields.js'
import { combineLabel } from '../components/repeat/rule.js'
import { Letterhead, themeOf, themeVars, type LetterheadBusiness } from '../components/proposal/Letterhead.js'
import { BRAND } from '../lib/brand.js'
import { cn } from '../lib/cn.js'
import { removeLogo, uploadLogo } from '../lib/files.js'
import { formatDateTime, relativeTime } from '../lib/format.js'
import { useCurrentUser, useSession } from '../lib/session.js'
import {
  errorMessage,
  fieldErrors,
  setBearerToken,
  useTRPC,
  type RouterOutputs,
} from '../lib/trpc.js'
import { BoardPanel } from './settings/BoardPanel.js'
import { CatalogPanel } from './settings/CatalogPanel.js'
import { ProposalsPanel } from './settings/ProposalsPanel.js'
import { ReportFormsPanel } from './settings/ReportFormsPanel.js'
import { ServiceAreasPanel } from './settings/ServiceAreasPanel.js'
import { TemplatesPanel } from './settings/TemplatesPanel.js'
import { UsersPanel } from './Users.js'

/**
 * Settings, as a section nav and a column of cards.
 *
 * The nav is 212px and the selected row is marked the way the job card marks
 * its tab: bold navy with a Buster Red edge (ADR 030). Sections appear only when there is
 * something behind them: a nav that lists Telephony and opens onto an empty
 * card teaches people the screen is a mock-up.
 *
 * Nothing here auto-saves. Every form is dirty-tracked and saved by an explicit
 * button, because these are account-wide values — a stray keystroke in the
 * booking line should not change what prints on every invoice. The switches
 * under Recurring jobs are the exception: a `Toggle` takes effect as it moves.
 *
 * The page is the board's `ink-100` field on every section. A per-tab surface
 * made switching People recolour the screen; the column is one page.
 */

type SectionId =
  | 'account'
  | 'business'
  | 'branches'
  | 'catalog'
  | 'reports'
  | 'proposals'
  | 'templates'
  | 'people'
  | 'scheduling'
  | 'areas'
  | 'migration'

interface Section {
  id: SectionId
  label: string
  permission?: 'USER_MANAGE' | 'SETTINGS_MANAGE' | 'PROPOSAL_TEMPLATE_MANAGE' | 'MESSAGE_TEMPLATE_MANAGE'
}

const SECTIONS: Section[] = [
  { id: 'account', label: 'Your account' },
  { id: 'business', label: 'Business details', permission: 'SETTINGS_MANAGE' },
  { id: 'branches', label: 'Branches', permission: 'SETTINGS_MANAGE' },
  { id: 'catalog', label: 'Catalog', permission: 'SETTINGS_MANAGE' },
  { id: 'reports', label: 'Report forms', permission: 'SETTINGS_MANAGE' },
  { id: 'proposals', label: 'Proposals', permission: 'PROPOSAL_TEMPLATE_MANAGE' },
  { id: 'templates', label: 'Templates', permission: 'MESSAGE_TEMPLATE_MANAGE' },
  { id: 'people', label: 'People and roles', permission: 'USER_MANAGE' },
  { id: 'scheduling', label: 'Scheduling', permission: 'USER_MANAGE' },
  { id: 'areas', label: 'Service areas', permission: 'SETTINGS_MANAGE' },
  { id: 'migration', label: 'Migration' },
]

export function Settings() {
  const { can } = useSession()
  const [searchParams, setSearchParams] = useSearchParams()

  const visible = SECTIONS.filter((section) => !section.permission || can(section.permission))
  const requested = searchParams.get('tab')
  const section = visible.find((entry) => entry.id === requested) ?? visible[0]
  const active: SectionId = section?.id ?? 'account'

  const setSection = (id: SectionId) => {
    const next = new URLSearchParams(searchParams)
    if (id === 'account') next.delete('tab')
    else next.set('tab', id)
    // `view` belongs to the People section's own pill, so carrying it onto
    // another tab would leave a parameter nothing reads.
    next.delete('view')
    setSearchParams(next, { replace: true })
  }

  return (
    <Page width="xwide" field>
      <SettingsHeader />

      <div className="flex flex-col items-stretch gap-[var(--stack-gap)] lg:flex-row lg:items-start">
        <Card className="lg:w-[212px] lg:flex-none">
          <ul className="divide-y divide-ink-100">
            {visible.map((section) => (
              <li key={section.id}>
                <SectionLink
                  label={section.label}
                  selected={section.id === active}
                  hint={section.id === 'migration' ? <MigrationHint /> : null}
                  onClick={() => setSection(section.id)}
                />
              </li>
            ))}
          </ul>
        </Card>

        <div className="flex min-w-0 flex-1 flex-col gap-[var(--stack-gap)]">
          {active === 'account' ? <AccountPanel /> : null}
          {active === 'business' ? <BusinessPanel /> : null}
          {active === 'branches' ? <BranchesPanel /> : null}
          {active === 'catalog' ? <CatalogPanel /> : null}
          {active === 'reports' ? <ReportFormsPanel /> : null}
          {active === 'proposals' ? <ProposalsPanel /> : null}
          {active === 'templates' ? <TemplatesPanel /> : null}
          {active === 'people' ? <UsersPanel /> : null}
          {active === 'scheduling' ? <SchedulingPanel /> : null}
          {active === 'areas' ? <ServiceAreasPanel /> : null}
          {active === 'migration' ? <MigrationPanel /> : null}
        </div>
      </div>
    </Page>
  )
}

/** Trading name, branch count and when the account record last changed. */
function SettingsHeader() {
  const trpc = useTRPC()
  const { can } = useSession()
  const enabled = can('SETTINGS_MANAGE')

  const settings = useQuery({ ...trpc.settings.get.queryOptions(), enabled })
  const branches = useQuery({ ...trpc.settings.branches.queryOptions(), enabled })

  const parts = [
    settings.data?.businessName,
    branches.data
      ? `${branches.data.length} ${branches.data.length === 1 ? 'branch' : 'branches'}`
      : null,
    settings.data ? `last saved ${formatDateTime(settings.data.updatedAt)}` : null,
  ].filter(Boolean)

  return (
    <CommandBar className="hidden desk:flex">
      <div className="min-w-0">
        <CommandTitle>Settings</CommandTitle>
        {parts.length > 0 ? <CommandMeta>{parts.join(' · ')}</CommandMeta> : null}
      </div>
    </CommandBar>
  )
}

function SectionLink({
  label,
  selected,
  hint,
  onClick,
}: {
  label: string
  selected: boolean
  hint: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
      className={cn(
        'flex min-h-[var(--control-md)] w-full items-center gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)] text-left text-sm transition-colors',
        selected
          ? 'bg-brand-50 font-bold text-brand-800 shadow-[inset_3px_0_0_var(--color-accent-500)]'
          : 'text-ink-700 hover:bg-ink-50 hover:text-brand-800',
      )}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint}
    </button>
  )
}

/** Amber on the nav row when the ServiceM8 mirror needs attention. */
function MigrationHint() {
  const trpc = useTRPC()
  const { data } = useQuery(trpc.system.importStatus.queryOptions())

  if (!data?.active || !data.isStale) return null
  return <span className="shrink-0 text-xs font-medium text-amber-700">Stale</span>
}

/* -------------------------------------------------------------------------- */

/**
 * Tracks a form against the record it came from.
 *
 * Returns the draft, a setter, whether anything actually differs, and a reset —
 * so Save can be disabled until there is something to save and Discard has an
 * unambiguous thing to go back to.
 */
function useDraft<T extends Record<string, string>>(source: T | undefined) {
  const [draft, setDraft] = useState<T | undefined>(source)

  // The record arrives after the first render, and can change under the form
  // when somebody else saves. Adopting it is right whenever the form is clean.
  useEffect(() => {
    setDraft((current) => (current === undefined ? source : current))
  }, [source])

  const value = draft ?? source
  const dirty =
    value !== undefined &&
    source !== undefined &&
    Object.keys(source).some((key) => value[key] !== source[key])

  return {
    value,
    dirty,
    set: (key: keyof T, next: string) =>
      setDraft((current) => ({ ...(current ?? source), [key]: next }) as T),
    reset: () => setDraft(source),
  }
}

function SaveBar({
  dirty,
  saving,
  saved,
  error,
  onDiscard,
}: {
  dirty: boolean
  saving: boolean
  saved: boolean
  error: string | null
  onDiscard: () => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {error ? <p className="mr-auto text-xs text-red-600">{error}</p> : null}
      {!error && saved && !dirty ? (
        <p className="mr-auto text-xs text-emerald-700">Saved.</p>
      ) : null}
      <Button variant="ghost" disabled={!dirty || saving} onClick={onDiscard}>
        Discard changes
      </Button>
      <Button type="submit" variant="primary" disabled={!dirty} loading={saving}>
        Save settings
      </Button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function BusinessPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const settings = useQuery(trpc.settings.get.queryOptions())

  const source = settings.data
    ? {
        businessName: settings.data.businessName,
        legalName: settings.data.legalName ?? '',
        taxNumber: settings.data.taxNumber ?? '',
        phone: settings.data.phone ?? '',
        email: settings.data.email ?? '',
        website: settings.data.website ?? '',
        inboundEmailAddress: settings.data.inboundEmailAddress ?? '',
        defaultPaymentTermsDays: String(settings.data.defaultPaymentTermsDays),
        pestControllerLicenceNumber: settings.data.pestControllerLicenceNumber ?? '',
        pestControllerLicenceExpiresAt: dateInputValue(
          settings.data.pestControllerLicenceExpiresAt,
        ),
        postalAddress: settings.data.postalAddress ?? '',
        bankAccountName: settings.data.bankAccountName ?? '',
        bankBsb: settings.data.bankBsb ?? '',
        bankAccountNumber: settings.data.bankAccountNumber ?? '',
        paymentInstructions: settings.data.paymentInstructions ?? '',
        defaultQuoteTerms: settings.data.defaultQuoteTerms ?? '',
        defaultInvoiceTerms: settings.data.defaultInvoiceTerms ?? '',
        documentFooter: settings.data.documentFooter ?? '',
        brandColour: settings.data.brandColour ?? '',
        accentColour: settings.data.accentColour ?? '',
      }
    : undefined

  const form = useDraft(source)

  const save = useMutation(
    trpc.settings.update.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.settings.pathFilter()),
    }),
  )

  if (settings.isPending || !form.value) {
    return (
      <Card>
        <CardHeader title="Business details" />
        <SkeletonRows rows={4} />
      </Card>
    )
  }

  const value = form.value
  const errors = fieldErrors(save.error)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    save.mutate({
      businessName: value.businessName,
      legalName: value.legalName,
      taxNumber: value.taxNumber,
      phone: value.phone,
      email: value.email,
      website: value.website,
      inboundEmailAddress: value.inboundEmailAddress,
      defaultPaymentTermsDays: Number(value.defaultPaymentTermsDays),
      pestControllerLicenceNumber: value.pestControllerLicenceNumber,
      pestControllerLicenceExpiresAt: value.pestControllerLicenceExpiresAt
        ? new Date(value.pestControllerLicenceExpiresAt)
        : null,
      postalAddress: value.postalAddress,
      bankAccountName: value.bankAccountName,
      bankBsb: value.bankBsb,
      bankAccountNumber: value.bankAccountNumber,
      paymentInstructions: value.paymentInstructions,
      defaultQuoteTerms: value.defaultQuoteTerms,
      defaultInvoiceTerms: value.defaultInvoiceTerms,
      documentFooter: value.documentFooter,
      brandColour: value.brandColour,
      accentColour: value.accentColour,
    })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-[var(--stack-gap)]" noValidate>
      <Card>
        <CardHeader title="Business details" />
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <Field label="Trading name" htmlFor="s-name" error={errors['businessName']} required>
            <Input
              id="s-name"
              value={value.businessName}
              onChange={(event) => form.set('businessName', event.target.value)}
            />
          </Field>

          <Field label="Legal name" htmlFor="s-legal" error={errors['legalName']}>
            <Input
              id="s-legal"
              value={value.legalName}
              onChange={(event) => form.set('legalName', event.target.value)}
            />
          </Field>

          <Field label="ABN" htmlFor="s-abn" error={errors['taxNumber']}>
            <Input
              id="s-abn"
              inputMode="numeric"
              value={value.taxNumber}
              onChange={(event) => form.set('taxNumber', event.target.value)}
            />
          </Field>

          <Field
            label="Booking line"
            htmlFor="s-phone"
            hint="Shown on quotes, invoices and SMS replies."
            error={errors['phone']}
          >
            <Input
              id="s-phone"
              type="tel"
              value={value.phone}
              onChange={(event) => form.set('phone', event.target.value)}
            />
          </Field>

          <Field label="Business email" htmlFor="s-email" error={errors['email']}>
            <Input
              id="s-email"
              type="email"
              value={value.email}
              onChange={(event) => form.set('email', event.target.value)}
            />
          </Field>

          <Field label="Website" htmlFor="s-web" error={errors['website']}>
            <Input
              id="s-web"
              value={value.website}
              onChange={(event) => form.set('website', event.target.value)}
            />
          </Field>

          <Field
            label="Inbound email address"
            htmlFor="s-inbound"
            hint="Customer email sent here lands in the Inbox."
            error={errors['inboundEmailAddress']}
          >
            <Input
              id="s-inbound"
              type="email"
              value={value.inboundEmailAddress}
              onChange={(event) => form.set('inboundEmailAddress', event.target.value)}
            />
          </Field>

          <Field
            label="Default payment terms"
            htmlFor="s-terms"
            hint="Days, applied to new clients."
            error={errors['defaultPaymentTermsDays']}
          >
            <Input
              id="s-terms"
              type="number"
              min={0}
              max={365}
              value={value.defaultPaymentTermsDays}
              onChange={(event) => form.set('defaultPaymentTermsDays', event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <BrandingCard
        letterhead={{
          name: value.businessName,
          address: value.postalAddress,
          phone: value.phone,
          email: value.email,
          website: value.website,
          taxNumber: value.taxNumber,
          logoUrl: settings.data?.logoUrl ?? null,
          brandColour: value.brandColour,
          accentColour: value.accentColour,
        }}
        errors={errors}
        onColour={(key, colour) => form.set(key, colour)}
      />

      <Card>
        <CardHeader title="Quotes and invoices" />
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <Field
            label="Letterhead address"
            htmlFor="s-postal"
            hint="Printed under the business name. One line each."
            error={errors['postalAddress']}
          >
            <Textarea
              id="s-postal"
              rows={3}
              value={value.postalAddress}
              onChange={(event) => form.set('postalAddress', event.target.value)}
            />
          </Field>

          <Field
            label="Footer"
            htmlFor="s-footer"
            hint="The small print at the foot of every page."
            error={errors['documentFooter']}
          >
            <Textarea
              id="s-footer"
              rows={3}
              value={value.documentFooter}
              onChange={(event) => form.set('documentFooter', event.target.value)}
            />
          </Field>

          <Field
            label="Default quote terms"
            htmlFor="s-quote-terms"
            hint="Copied onto a new quote, which then keeps its own copy."
            error={errors['defaultQuoteTerms']}
          >
            <Textarea
              id="s-quote-terms"
              rows={4}
              value={value.defaultQuoteTerms}
              onChange={(event) => form.set('defaultQuoteTerms', event.target.value)}
            />
          </Field>

          <Field
            label="Default invoice terms"
            htmlFor="s-invoice-terms"
            hint="Editing these never changes a document already issued."
            error={errors['defaultInvoiceTerms']}
          >
            <Textarea
              id="s-invoice-terms"
              rows={4}
              value={value.defaultInvoiceTerms}
              onChange={(event) => form.set('defaultInvoiceTerms', event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader title="How to pay" />
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <Field
            label="Account name"
            htmlFor="s-bank-name"
            error={errors['bankAccountName']}
          >
            <Input
              id="s-bank-name"
              value={value.bankAccountName}
              onChange={(event) => form.set('bankAccountName', event.target.value)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="BSB" htmlFor="s-bsb" error={errors['bankBsb']}>
              <Input
                id="s-bsb"
                inputMode="numeric"
                autoComplete="off"
                value={value.bankBsb}
                onChange={(event) => form.set('bankBsb', event.target.value)}
              />
            </Field>

            <Field
              label="Account number"
              htmlFor="s-acct"
              error={errors['bankAccountNumber']}
            >
              <Input
                id="s-acct"
                inputMode="numeric"
                autoComplete="off"
                value={value.bankAccountNumber}
                onChange={(event) => form.set('bankAccountNumber', event.target.value)}
              />
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field
              label="Payment instructions"
              htmlFor="s-pay-note"
              hint="Anything else the block should say — a payment link, or to quote the invoice number."
              error={errors['paymentInstructions']}
            >
              <Textarea
                id="s-pay-note"
                rows={2}
                value={value.paymentInstructions}
                onChange={(event) => form.set('paymentInstructions', event.target.value)}
              />
            </Field>
          </div>
        </div>
        <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
          Leave these blank and an invoice simply prints without a payment block. The terms above
          are copied onto a document when it is created — editing them never rewrites what a
          customer has already been sent.
        </p>
      </Card>

      <Card>
        <CardHeader title="Pest control licence" />
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
          <Field
            label="Pest Controller's Licence number"
            htmlFor="s-pcl"
            hint="The business licence, not a technician's card."
            error={errors['pestControllerLicenceNumber']}
          >
            <Input
              id="s-pcl"
              value={value.pestControllerLicenceNumber}
              onChange={(event) => form.set('pestControllerLicenceNumber', event.target.value)}
            />
          </Field>

          <Field
            label="Expires"
            htmlFor="s-pcl-expiry"
            error={errors['pestControllerLicenceExpiresAt']}
          >
            <Input
              id="s-pcl-expiry"
              type="date"
              value={value.pestControllerLicenceExpiresAt}
              onChange={(event) => form.set('pestControllerLicenceExpiresAt', event.target.value)}
            />
          </Field>
        </div>
        <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
          Every technician works under this licence, so if it lapses they are all unlicensed
          whatever their own cards say. It is shown as a banner above the certifications list.
        </p>
      </Card>

      <SaveBar
        dirty={form.dirty}
        saving={save.isPending}
        saved={save.isSuccess}
        error={save.error ? errorMessage(save.error) : null}
        onDiscard={form.reset}
      />
    </form>
  )
}

/**
 * The proposal's letterhead: the logo, two colours, and what they look like.
 *
 * The logo uploads the moment it is picked — it is a file, not a field, and
 * `POST /files/logo` writes it — while the colours are part of the form and
 * wait for Save like everything else on this page. Decision 81.
 */
function BrandingCard({
  letterhead,
  errors,
  onColour,
}: {
  letterhead: LetterheadBusiness
  errors: ReturnType<typeof fieldErrors>
  onColour: (key: 'brandColour' | 'accentColour', colour: string) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const theme = themeOf(letterhead)

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries(trpc.settings.get.pathFilter()),
      queryClient.invalidateQueries(trpc.settings.letterhead.pathFilter()),
    ])

  const upload = async (file: File | undefined) => {
    if (!file) return
    setProblem(null)
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      setProblem('Use a PNG or a JPEG. The PDF cannot embed an SVG — export one from it at twice the size you need.')
      return
    }
    setBusy(true)
    try {
      await uploadLogo(file)
      await refresh()
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The logo did not upload.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await removeLogo()
      await refresh()
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The logo was not removed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader title="Branding" />
      <div className="grid grid-cols-1 gap-5 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <div className="flex flex-col gap-4">
          <div>
            <p className="mb-1.5 text-sm font-medium text-ink-800">Logo</p>
            <label
              onDragOver={(event) => {
                event.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault()
                setDragging(false)
                void upload(event.dataTransfer.files[0])
              }}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-5 text-center text-sm transition-colors',
                dragging ? 'border-brand-600 bg-brand-50' : 'border-ink-300 bg-ink-50 hover:border-ink-400',
              )}
            >
              <input
                type="file"
                accept="image/png,image/jpeg"
                aria-label="Upload a logo"
                className="sr-only"
                disabled={busy}
                onChange={(event) => {
                  void upload(event.target.files?.[0])
                  event.target.value = ''
                }}
              />
              <span className="font-medium text-ink-800">
                {busy ? 'Uploading…' : letterhead.logoUrl ? 'Replace the logo' : 'Drop a logo here, or choose one'}
              </span>
              <span className="text-xs text-ink-500">PNG or JPEG, on a white or clear background.</span>
            </label>
            {letterhead.logoUrl ? (
              <Button type="button" size="sm" variant="ghost" className="mt-1" disabled={busy} onClick={() => void remove()}>
                Remove the logo
              </Button>
            ) : null}
            {problem ? (
              <p role="alert" className="mt-1.5 text-xs text-red-600">
                {problem}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ColourField
              id="s-brand"
              label="Brand colour"
              hint="Headings, the chosen option."
              value={letterhead.brandColour ?? ''}
              fallback={theme.brand}
              error={errors['brandColour']}
              onChange={(colour) => onColour('brandColour', colour)}
            />
            <ColourField
              id="s-accent"
              label="Second colour"
              hint="The letterhead's small print."
              value={letterhead.accentColour ?? ''}
              fallback={theme.accent}
              error={errors['accentColour']}
              onChange={(colour) => onColour('accentColour', colour)}
            />
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium text-ink-800">How a proposal starts</p>
          <div
            style={themeVars(theme)}
            className="overflow-hidden rounded-lg border border-ink-200 bg-white shadow-sm"
          >
            <div className="h-1.5 bg-[var(--p-brand)]" />
            <div className="p-5 font-proposal">
              <Letterhead business={letterhead} number="QTE-00123" date="23rd September 2026" compact />
              <p className="mt-3 text-lg leading-tight font-bold text-[var(--p-brand)]">
                Commercial Pest Control Program Agreement
              </p>
              <p className="mt-2 border-b border-[var(--p-brand)] pb-1 text-xs font-bold text-ink-900">
                Control methods where covered
              </p>
              <p className="mt-1.5 text-[11px] leading-4 text-ink-700">
                Rodents will be controlled with fixed tamper resistant stations and monitors.
              </p>
            </div>
          </div>
        </div>
      </div>
      <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
        Used on proposals — the builder, the customer&apos;s page and the PDF. Quotes and invoices keep their plain
        letterhead. Blank colours use the Bug Busters navy and ink.
      </p>
    </Card>
  )
}

function ColourField({
  id,
  label,
  hint,
  value,
  fallback,
  error,
  onChange,
}: {
  id: string
  label: string
  hint: string
  value: string
  fallback: string
  error: ReturnType<typeof fieldErrors>[string] | undefined
  onChange: (colour: string) => void
}) {
  return (
    <Field label={label} htmlFor={id} hint={hint} {...(error ? { error } : {})}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} swatch`}
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback}
          onChange={(event) => onChange(event.target.value)}
          className="size-9 shrink-0 cursor-pointer rounded-md border border-ink-200 bg-white p-0.5"
        />
        <Input id={id} value={value} placeholder={fallback} onChange={(event) => onChange(event.target.value)} />
      </div>
    </Field>
  )
}

/** A `Date` as the `yyyy-mm-dd` an `<input type="date">` wants. */
function dateInputValue(value: Date | string | null | undefined): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  // Local parts, not `toISOString`: a date stored at midnight Perth time
  // renders as the previous day in UTC, and a licence that expires on the 30th
  // must not show the 29th.
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

/* -------------------------------------------------------------------------- */

function BranchesPanel() {
  const trpc = useTRPC()
  const { data, isPending } = useQuery(trpc.settings.branches.queryOptions())

  return (
    <Card>
      <CardHeader title="Branches" />
      {isPending ? (
        <SkeletonRows rows={2} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="No branches"
          description="Every job belongs to a branch. They arrive with the ServiceM8 import."
        />
      ) : (
        <ul className="divide-y divide-ink-100">
          {data.map((branch) => (
            <li
              key={branch.id}
              className="flex items-center gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
            >
              <span className="min-w-0 flex-1 truncate text-sm text-ink-900">{branch.name}</span>
              {branch.code ? <Badge>{branch.code}</Badge> : null}
            </li>
          ))}
        </ul>
      )}
      <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
        Branches are read-only here. They come from ServiceM8 and are edited there until the dual
        run ends.
      </p>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */

/** Quarter-hours across a working day, as minutes from midnight. */
const DAY_TIMES = Array.from({ length: 4 * 24 }, (_, index) => index * 15)

function timeLabel(minutes: number): string {
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60
  const suffix = hour < 12 ? 'am' : 'pm'
  const twelve = hour % 12 === 0 ? 12 : hour % 12
  return `${twelve}:${String(minute).padStart(2, '0')} ${suffix}`
}

/**
 * The field rules, as switches.
 *
 * Two have a column and a rule on the server behind them. The SMS reminder
 * is drawn and disabled, because there is no outbound SMS yet and a switch
 * that saves and then does nothing is worse than one that plainly says why it
 * cannot move.
 */
function SchedulingPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const settings = useQuery({
    ...trpc.settings.get.queryOptions(),
    enabled: can('SETTINGS_MANAGE'),
  })

  const source = settings.data
    ? {
        workdayStartMinutes: String(settings.data.workdayStartMinutes),
        workdayEndMinutes: String(settings.data.workdayEndMinutes),
      }
    : undefined

  const form = useDraft(source)

  const save = useMutation(
    trpc.settings.update.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.settings.pathFilter()),
    }),
  )

  const value = form.value

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!value) return
    save.mutate({
      workdayStartMinutes: Number(value.workdayStartMinutes),
      workdayEndMinutes: Number(value.workdayEndMinutes),
    })
  }

  const errors = fieldErrors(save.error)

  return (
    <>
      {can('SETTINGS_MANAGE') ? (
        <form onSubmit={submit} className="flex flex-col gap-[var(--stack-gap)]" noValidate>
          <Card>
            <CardHeader title="Working day" />
            {!value ? (
              <SkeletonRows rows={2} />
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                <Field label="Working day starts" htmlFor="s-day-start">
                  <Select
                    id="s-day-start"
                    value={value.workdayStartMinutes}
                    onChange={(event) => form.set('workdayStartMinutes', event.target.value)}
                  >
                    {DAY_TIMES.map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {timeLabel(minutes)}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field
                  label="Working day ends"
                  htmlFor="s-day-end"
                  hint="Sets the hours the dispatch board draws."
                  error={errors['workdayEndMinutes']}
                >
                  <Select
                    id="s-day-end"
                    value={value.workdayEndMinutes}
                    onChange={(event) => form.set('workdayEndMinutes', event.target.value)}
                  >
                    {DAY_TIMES.map((minutes) => (
                      <option key={minutes} value={minutes}>
                        {timeLabel(minutes)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            )}
          </Card>

          <SaveBar
            dirty={form.dirty}
            saving={save.isPending}
            saved={save.isSuccess}
            error={save.error ? errorMessage(save.error) : null}
            onDiscard={form.reset}
          />
        </form>
      ) : null}

      {can('SETTINGS_MANAGE') ? <FieldRulesCard /> : null}

      {can('SETTINGS_MANAGE') ? <RecurringJobsCard /> : null}

      <BoardPanel />

    </>
  )
}

/**
 * Field rules: switches with a rule on the server behind each. Require photos
 * refuses to complete a visit, from the phone or the board, until a photo is
 * on the job (`assertChecklistDone`). It writes the moment it moves, like the
 * recurring-job switches below.
 *
 * There is no "technicians see job values" switch: technicians bill on site
 * (they hold QUOTE_VIEW and INVOICE_VIEW), so hiding the money would stop the
 * upsell and the payment at the door.
 */
function FieldRulesCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const settings = useQuery(trpc.settings.get.queryOptions())

  const save = useMutation(
    trpc.settings.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.settings.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      },
    }),
  )

  const data = settings.data

  return (
    <Card>
      <CardHeader title="Field rules" />
      {settings.isPending || !data ? (
        <SkeletonRows rows={1} />
      ) : (
        <ul className="divide-y divide-ink-100">
          <li className="px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
            <ToggleRow
              label="Require photos before completing a visit"
              description="A visit cannot be completed — from the phone or the board — until a photo of the work is on the job."
              checked={data.requirePhotosToComplete}
              disabled={save.isPending}
              onChange={(next) => save.mutate({ requirePhotosToComplete: next })}
            />
          </li>
        </ul>
      )}
      {save.error ? <ErrorState message={errorMessage(save.error)} /> : null}
    </Card>
  )
}

const LEAD_DAYS = [7, 14, 28, 42, 56] as const
const COMBINE_DAYS = [0, 3, 7, 14, 28] as const

/** The choice's value for the same month, beside the day counts. */
const SAME_MONTH = 'month'

function combineChoiceLabel(days: number): string {
  const label = combineLabel({ combineMonth: false, combineDays: days })
  return label ? label.replace(/^./, (letter) => letter.toUpperCase()) : 'Never'
}

/**
 * Recurring jobs: how far ahead a repeat schedule makes its next job, whether
 * that job is booked in, and the master switch for making them at all.
 * Decision 96.
 *
 * Each switch writes the moment it moves, like every `Toggle`. Automatic
 * creation stays off until the ServiceM8 cutover: while ServiceM8 still makes
 * its own recurring jobs, both systems making the next one would book the
 * customer twice. The client reminder is drawn and inert, because there is no
 * outbound SMS provider behind it.
 */
function RecurringJobsCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const settings = useQuery(trpc.settings.get.queryOptions())
  const [confirming, setConfirming] = useState(false)

  const save = useMutation(
    trpc.settings.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.settings.pathFilter())
        void queryClient.invalidateQueries(trpc.repeat.pathFilter())
      },
    }),
  )

  const data = settings.data
  const leadDays = data?.repeatLeadDays ?? 28
  const autoBook = data?.repeatAutoBook ?? true
  const autoCreate = data?.repeatAutoCreate ?? false
  const combineDays = data?.repeatCombineDays ?? 14
  const combineMonth = data?.repeatCombineMonth ?? true
  const combineOptions = [
    { value: SAME_MONTH, label: 'In the same month' },
    ...(COMBINE_DAYS.some((days) => days === combineDays) ? [...COMBINE_DAYS] : [...COMBINE_DAYS, combineDays])
      .sort((a, b) => a - b)
      .map((days) => ({ value: String(days), label: combineChoiceLabel(days) })),
  ]

  return (
    <Card>
      <CardHeader title="Recurring jobs" />
      {settings.isPending ? (
        <SkeletonRows rows={3} />
      ) : (
        <ul className="divide-y divide-ink-100">
          <li className="flex flex-col gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
            <span className="text-sm font-medium text-ink-900">Create the next job</span>
            <span className="text-xs text-ink-500">
              How long before a visit is due its job is made, so there is time to book it in.
            </span>
            <Choice
              label="Create the next job"
              value={String(leadDays)}
              options={LEAD_DAYS.map((days) => ({
                value: String(days),
                label: `${days / 7} week${days === 7 ? '' : 's'} before`,
              }))}
              onChange={(value) => save.mutate({ repeatLeadDays: Number(value) })}
            />
          </li>

          <li className="flex flex-col gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
            <span className="text-sm font-medium text-ink-900">Put visits on one job</span>
            <span className="text-xs text-ink-500">
              When an agreement has several services at one site due close together, they are made as
              one job with every service and its instructions on it, so it is one trip.
            </span>
            <Choice
              label="Put visits on one job"
              value={combineMonth ? SAME_MONTH : String(combineDays)}
              options={combineOptions}
              onChange={(value) =>
                save.mutate(
                  value === SAME_MONTH
                    ? { repeatCombineMonth: true }
                    : { repeatCombineMonth: false, repeatCombineDays: Number(value) },
                )
              }
            />
          </li>

          <li className="flex items-start gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-ink-900">Book it in automatically</span>
              <span className="mt-px block text-xs text-ink-500">
                With the technician the schedule names, on the due day at the last visit’s time.
                Booked as proposed, so the board can still move it.
              </span>
            </span>
            <Toggle
              label="Book it in automatically"
              checked={autoBook}
              disabled={save.isPending}
              onChange={(next) => save.mutate({ repeatAutoBook: next })}
            />
          </li>

          <li className="flex flex-col gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
            <div className="flex items-start gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink-900">
                  Create recurring jobs automatically
                </span>
                <span className="mt-px block text-xs text-ink-500">
                  {autoCreate
                    ? 'On. Each schedule’s next job is made when it falls due to be.'
                    : 'Off until ServiceM8 is switched off. Until then, make each job from its Schedule tab.'}
                </span>
              </span>
              <Toggle
                label="Create recurring jobs automatically"
                checked={autoCreate}
                disabled={save.isPending}
                onChange={(next) => (next ? setConfirming(true) : save.mutate({ repeatAutoCreate: false }))}
              />
            </div>
            {confirming ? (
              <div
                role="alert"
                className="flex flex-col gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-amber-200 ring-inset"
              >
                <p>
                  ServiceM8 still makes its own recurring jobs. With both on, a customer gets two
                  jobs for one visit. Turn this on only once ServiceM8 has stopped.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setConfirming(false)}>
                    Leave it off
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={save.isPending}
                    onClick={() =>
                      save.mutate({ repeatAutoCreate: true }, { onSuccess: () => setConfirming(false) })
                    }
                  >
                    Turn it on
                  </Button>
                </div>
              </div>
            ) : null}
          </li>

          <li className="flex items-start gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-ink-500">Remind the client by SMS</span>
              <span className="mt-px block text-xs text-ink-400">
                Needs the outbound SMS provider, which is not connected yet.
              </span>
            </span>
            <Toggle label="Remind the client by SMS" checked={false} disabled onChange={() => undefined} />
          </li>
        </ul>
      )}
      {save.error ? <ErrorState message={errorMessage(save.error)} /> : null}
    </Card>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * The ServiceM8 mirror: how fresh it is, and the button that refreshes it.
 *
 * The button only queues. The sync worker — a separate process — claims the
 * request, and this panel follows its progress by polling while one is queued
 * or running, and not otherwise.
 */
function MigrationPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const [confirming, setConfirming] = useState(false)

  const { data, isPending, error } = useQuery({
    ...trpc.system.importStatus.queryOptions(),
    refetchInterval: (query) => (query.state.data?.current ? 2_000 : false),
  })

  const request = useMutation(
    trpc.system.requestSync.mutationOptions({
      onSuccess: () => {
        setConfirming(false)
        void queryClient.invalidateQueries(trpc.system.pathFilter())
      },
    }),
  )

  if (error) {
    return (
      <Card>
        <CardHeader title="Migration from ServiceM8" />
        <p role="alert" className="px-[var(--row-pad-x)] py-3 text-sm text-red-700">
          Could not read the sync status: {errorMessage(error)}
        </p>
      </Card>
    )
  }

  if (isPending || !data) {
    return (
      <Card>
        <CardHeader title="Migration from ServiceM8" />
        <SkeletonRows rows={3} />
      </Card>
    )
  }

  const { current, last } = data
  const everSynced = data.at !== null || last !== null

  return (
    <Card>
      <CardHeader
        title="Migration from ServiceM8"
        action={
          current?.status === 'QUEUED' ? (
            <Badge tone="blue">Queued</Badge>
          ) : current ? (
            <Badge tone="blue">Running</Badge>
          ) : !everSynced ? (
            <Badge>Never synced</Badge>
          ) : data.isStale ? (
            <Badge tone="amber">Not synced today</Badge>
          ) : (
            <Badge tone="green">Synced today</Badge>
          )
        }
      />
      <ul className="divide-y divide-ink-100">
        <StatRow label="Last sync finished">
          {data.at ? `${formatDateTime(data.at)} · ${relativeTime(data.at)}` : 'Never'}
        </StatRow>
        {data.jobs > 0 ? (
          <StatRow label="Jobs in the sync window">{data.jobs.toLocaleString('en-AU')}</StatRow>
        ) : null}
      </ul>

      {current ? <SyncProgressView current={current} /> : last ? <LastSync last={last} /> : null}

      {can('SETTINGS_MANAGE') ? (
        <div className="flex flex-col gap-2 border-t border-ink-200 px-[var(--row-pad-x)] py-3">
          {confirming && !current ? (
            <>
              <p className="text-sm text-ink-800">
                This replaces every job from three days ago onward with what ServiceM8 has. Changes
                made in {BRAND.name} to those jobs are lost.
                {everSynced ? null : ' The first sync takes about half an hour.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={request.isPending}
                  onClick={() => request.mutate()}
                >
                  Sync now
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div>
              <Button
                variant="primary"
                disabled={current !== null}
                onClick={() => {
                  request.reset()
                  setConfirming(true)
                }}
              >
                Sync from ServiceM8
              </Button>
            </div>
          )}
          {request.error ? (
            <p className="text-xs text-red-600" role="alert">
              {errorMessage(request.error)}
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
        A sync fetches what changed in ServiceM8, rebuilds the jobs from three days back onward, and
        fetches their files. Anything typed into {BRAND.name} against those jobs does not survive it.
      </p>
    </Card>
  )
}

type CurrentSync = NonNullable<RouterOutputs['system']['importStatus']['current']>
type LastSyncResult = NonNullable<RouterOutputs['system']['importStatus']['last']>

/** A request waits for the worker; past this it is probably not running. */
const WORKER_SLOW_MS = 60_000

function SyncProgressView({ current }: { current: CurrentSync }) {
  if (current.status === 'QUEUED') {
    const waited = Date.now() - new Date(current.requestedAt).getTime()
    return (
      <div className="border-t border-ink-200 px-[var(--row-pad-x)] py-3 text-sm text-ink-700">
        {waited > WORKER_SLOW_MS
          ? 'Waiting for the sync worker. Nothing has picked this up yet, so the worker may not be running.'
          : 'Waiting for the sync worker to pick this up…'}
      </div>
    )
  }

  const progress = current.progress
  const at = progress ? SYNC_STAGES.findIndex((entry) => entry.stage === progress.stage) : 0

  return (
    <ol aria-label="Sync progress" className="divide-y divide-ink-100 border-t border-ink-200">
      {SYNC_STAGES.map((entry, index) => {
        const state = index < at ? 'done' : index === at ? 'active' : 'waiting'
        const detail = state === 'active' && progress ? progressDetail(progress) : null
        const fraction =
          state === 'active' && progress?.total ? Math.min(1, progress.done / progress.total) : null

        return (
          <li
            key={entry.stage}
            aria-current={state === 'active' ? 'step' : undefined}
            className="flex flex-col gap-1.5 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  state === 'done' && 'bg-emerald-600',
                  state === 'active' && 'bg-blue-600',
                  state === 'waiting' && 'bg-ink-300',
                )}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 text-sm',
                  state === 'waiting' ? 'text-ink-400' : 'text-ink-800',
                )}
              >
                {entry.label}
              </span>
              <span className="text-xs tabular-nums text-ink-500">
                {state === 'done' ? 'Done' : detail}
              </span>
            </div>
            {fraction !== null ? (
              <div
                role="progressbar"
                aria-label={entry.label}
                aria-valuemin={0}
                aria-valuemax={progress!.total!}
                aria-valuenow={progress!.done}
                className="ml-5 h-1 overflow-hidden rounded-full bg-ink-100"
              >
                <div className="h-full bg-blue-600" style={{ width: `${fraction * 100}%` }} />
              </div>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

function progressDetail(progress: SyncProgress): string {
  const parts = [progress.label]
  if (progress.total) parts.push(`${progress.done.toLocaleString('en-AU')} of ${progress.total.toLocaleString('en-AU')}`)
  if (progress.count !== undefined) parts.push(`${progress.count.toLocaleString('en-AU')} records`)
  return parts.join(' · ')
}

function LastSync({ last }: { last: LastSyncResult }) {
  if (last.status === 'SUCCEEDED') {
    const summary = last.summary
    if (!summary) return null

    const parts = [`${summary.jobs.toLocaleString('en-AU')} jobs synced`]
    if (summary.loadFailures > 0) {
      parts.push(`${summary.loadFailures.toLocaleString('en-AU')} rows could not be loaded`)
    }
    if (summary.files) {
      parts.push(`${summary.files.transferred.toLocaleString('en-AU')} files fetched`)
      if (summary.files.remaining > 0) {
        parts.push(`${summary.files.remaining.toLocaleString('en-AU')} still to fetch`)
      }
    }

    return (
      <div className="border-t border-ink-200 px-[var(--row-pad-x)] py-3">
        <p className="text-sm text-ink-800">{parts.join(' · ')}</p>
        {summary.files?.stopReason ? (
          <p className="mt-1 text-xs text-amber-800">Files stopped early: {summary.files.stopReason}</p>
        ) : null}
      </div>
    )
  }

  if (last.status === 'RATE_LIMITED') {
    return (
      <p className="border-t border-ink-200 bg-amber-50 px-[var(--row-pad-x)] py-3 text-sm text-amber-800">
        The last sync stopped because the day&apos;s ServiceM8 request budget ran out. What it
        fetched is kept; sync again tomorrow to carry on.
      </p>
    )
  }

  const stage = SYNC_STAGES.find((entry) => entry.stage === last.failedAt)
  return (
    <p
      role="alert"
      className="border-t border-ink-200 bg-red-50 px-[var(--row-pad-x)] py-3 text-sm text-red-700"
    >
      The last sync failed
      {stage ? ` while ${stage.label.charAt(0).toLowerCase()}${stage.label.slice(1)}` : ''}
      {last.error ? `: ${last.error}` : '.'}
    </p>
  )
}

function StatRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <li className="flex items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]">
      <span className="min-w-0 flex-1 text-sm text-ink-700">{label}</span>
      <span className="text-sm font-medium tabular-nums text-ink-900">{children}</span>
    </li>
  )
}

/* -------------------------------------------------------------------------- */

function AccountPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const me = useCurrentUser()

  const sessions = useQuery(trpc.auth.sessions.queryOptions())

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')

  const changePassword = useMutation(
    trpc.auth.changePassword.mutationOptions({
      onSuccess: () => {
        setCurrent('')
        setNext('')
        void queryClient.invalidateQueries(trpc.auth.pathFilter())
      },
    }),
  )

  const revoke = useMutation(
    trpc.auth.revokeSession.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.auth.pathFilter()),
    }),
  )

  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: async () => {
        setBearerToken(null)
        queryClient.clear()
        navigate('/login', { replace: true })
      },
    }),
  )

  const submitPassword = (event: FormEvent) => {
    event.preventDefault()
    changePassword.mutate({ currentPassword: current, newPassword: next })
  }

  const passwordErrors = fieldErrors(changePassword.error)

  return (
    <>
      <Card>
        <div className="flex items-center gap-3 p-[var(--card-pad)]">
          <Avatar name={me.name} colour={me.colour} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink-900">{me.name}</p>
            <p className="truncate text-xs text-ink-500">{me.email}</p>
            <Badge className="mt-1">{me.roleName}</Badge>
          </div>
          <Button variant="ghost" loading={logout.isPending} onClick={() => logout.mutate()}>
            Sign out
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Change password" />
        <form
          onSubmit={submitPassword}
          className="flex flex-col gap-3 p-[var(--card-pad)]"
          noValidate
        >
          <Field label="Current password" htmlFor="current" required>
            <Input
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>

          <Field
            label="New password"
            htmlFor="next"
            hint="At least 12 characters. This signs out your other devices."
            error={passwordErrors['newPassword']}
            required
          >
            <Input
              id="next"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>

          {changePassword.error ? (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage(changePassword.error)}
            </p>
          ) : null}
          {changePassword.isSuccess ? (
            <p className="text-sm text-emerald-700">Password changed.</p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            className="self-start"
            loading={changePassword.isPending}
          >
            Change password
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader title="Signed-in devices" />
        {sessions.isPending ? (
          <SkeletonRows rows={2} />
        ) : (
          <ul className="divide-y divide-ink-100">
            {(sessions.data ?? []).map((session) => (
              <li
                key={session.id}
                className="flex items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
              >
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="text-sm font-medium text-ink-900">
                    {session.deviceName ?? 'Unknown device'}
                  </span>
                  {session.isCurrent ? <Badge tone="green">This device</Badge> : null}
                  <span className="text-xs text-ink-500">
                    Signed in {relativeTime(session.createdAt)} · expires{' '}
                    {formatDateTime(session.expiresAt)}
                  </span>
                </span>

                {session.isCurrent ? null : (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={revoke.isPending}
                    onClick={() => revoke.mutate({ id: session.id })}
                  >
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}
